import React, { useCallback, useEffect, useState, useRef, memo, createElement, Component } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { UserIcon, MailIcon, PhoneIcon, MapPinIcon, CheckCircleIcon, EditIcon, SaveIcon, UploadIcon, LoaderIcon, ClockIcon, AlertCircleIcon, InfoIcon, XIcon, TrashIcon, Trash2Icon, Camera } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable, StorageError, deleteObject } from 'firebase/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import { syncPublicProfile, fullSyncPublicProfile } from '../../services/publicProfileService';
// Add debug flag - can be toggled via localStorage for troubleshooting
const DEBUG_MODE = localStorage.getItem('debugImageUpload') === 'true';
// Image compression configuration
const IMAGE_COMPRESSION = {
  maxWidth: 800,
  maxHeight: 800,
  quality: 0.85,
  format: 'image/jpeg' // Output format
};
// Upload configuration
const UPLOAD_CONFIG = {
  timeout: 15000,
  useParallelUpload: true // Enable parallel upload to Firebase and localStorage
};
// Local Storage Keys
const LOCAL_STORAGE_PREFIX = 'maestroConnect_';
const PROFILE_IMAGE_KEY = `${LOCAL_STORAGE_PREFIX}profileImage`;
export const MyInfoPage = () => {
  const {
    userType,
    currentUser,
    updateUserProfile
  } = useAuth();
  const {
    theme
  } = useTheme();
  const darkMode = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  // State for user data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // State for address fields
  const [street, setStreet] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  // State for edit modes
  const [personalInfoEditMode, setPersonalInfoEditMode] = useState(false);
  const [addressEditMode, setAddressEditMode] = useState(false);
  // State for save status
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // State for form dirty tracking
  const [formDirty, setFormDirty] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const lastFocusedElement = useRef(null);
  // State for profile photo
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [useLocalStorage, setUseLocalStorage] = useState(false);
  const [showDeletePhotoConfirm, setShowDeletePhotoConfirm] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  // State for validation errors
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Banner upload refs and state
  const bannerFileInputRef = useRef<HTMLInputElement>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerFileName, setBannerFileName] = useState<string | null>(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null);
  const [isDraggingBanner, setIsDraggingBanner] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [isDeletingBanner, setIsDeletingBanner] = useState(false);
  const [showDeleteBannerConfirm, setShowDeleteBannerConfirm] = useState(false);
  const [showBannerMenu, setShowBannerMenu] = useState(false);
  const [isDraggingProfilePhoto, setIsDraggingProfilePhoto] = useState(false);
  // Ref for upload timeout
  const uploadTimeoutRef = useRef<number | null>(null);
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  // Ref for storing active upload task
  const activeUploadTask = useRef<any>(null);
  // Track original values for dirty checking
  const originalValues = useRef({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    addressLine2: '',
    city: '',
    zipCode: '',
    state: '',
    country: ''
  });
  // Check if form is dirty by comparing current values with original values
  const checkFormDirty = useCallback(() => {
    const isDirty = firstName !== originalValues.current.firstName || lastName !== originalValues.current.lastName || email !== originalValues.current.email || phone !== originalValues.current.phone || street !== originalValues.current.street || addressLine2 !== originalValues.current.addressLine2 || city !== originalValues.current.city || zipCode !== originalValues.current.zipCode || state !== originalValues.current.state || country !== originalValues.current.country;
    setFormDirty(isDirty);
  }, [firstName, lastName, email, phone, street, addressLine2, city, zipCode, state, country]);
  // Update form dirty state whenever form values change
  useEffect(() => {
    if (!isLoading) {
      checkFormDirty();
    }
  }, [firstName, lastName, email, phone, street, addressLine2, city, zipCode, state, country, isLoading, checkFormDirty]);
  // Load user data when component mounts
  useEffect(() => {
    // Ensure isMounted is true when component mounts
    isMounted.current = true;
    const fetchUserData = async () => {
      if (!currentUser) return;
      setIsLoading(true);
      try {
        // Check if we have a locally stored profile image
        let localStorageImageTimestamp = 0;
        let firestoreImageTimestamp = 0;
        let localProfileImage = null;
        if (currentUser.uid) {
          // Get local storage image and its timestamp
          const localImageData = localStorage.getItem(`${PROFILE_IMAGE_KEY}_${currentUser.uid}`);
          if (localImageData) {
            try {
              const parsedData = JSON.parse(localImageData);
              localProfileImage = parsedData.imageUrl;
              localStorageImageTimestamp = parsedData.timestamp || 0;
            } catch (e) {
              // Handle legacy format (string only, no timestamp)
              localProfileImage = localImageData;
            }
          }
        }
        // First try to get data from teachers collection
        console.log('[Data Load] Fetching from teachers collection for user:', currentUser.uid);
        const teacherDocRef = doc(db, 'teachers', currentUser.uid);
        const teacherDoc = await getDoc(teacherDocRef);
        if (teacherDoc.exists()) {
          console.log('[Data Load] Teacher document found');
          const teacherData = teacherDoc.data();
          setFirstName(teacherData.firstName || '');
          setLastName(teacherData.lastName || '');
          setEmail(teacherData.email || '');
          // DEFENSIVE PARSING: Handle city field that may contain "City, State" format
          let loadedCity = teacherData.city || '';
          let loadedState = teacherData.state || '';
          // If city contains a comma, it likely has the full "City, State" format
          // Parse it to extract city and state separately
          if (loadedCity.includes(',')) {
            const parts = loadedCity.split(',');
            loadedCity = parts[0]?.trim() || '';
            // Only use parsed state if current state is empty
            if (!loadedState && parts.length > 1) {
              loadedState = parts[1]?.trim() || '';
            }
          }
          setCity(loadedCity);
          setState(loadedState);
          setPhone(teacherData.phone || '');
          setStreet(teacherData.street || '');
          setAddressLine2(teacherData.addressLine2 || '');
          setZipCode(teacherData.zipCode || '');
          setCountry(teacherData.country || '');
          // Load banner image if exists
          console.log('[Banner Load] Teacher data bannerImageUrl:', teacherData.bannerImageUrl);
          if (teacherData.bannerImageUrl) {
            console.log('[Banner Load] Setting banner preview URL to:', teacherData.bannerImageUrl);
            setBannerPreviewUrl(teacherData.bannerImageUrl);
          } else {
            console.log('[Banner Load] No banner image found in Firestore');
          }
          // Store original values for dirty checking
          originalValues.current = {
            firstName: teacherData.firstName || '',
            lastName: teacherData.lastName || '',
            email: teacherData.email || '',
            phone: teacherData.phone || '',
            street: teacherData.street || '',
            addressLine2: teacherData.addressLine2 || '',
            city: loadedCity,
            zipCode: teacherData.zipCode || '',
            state: loadedState,
            country: teacherData.country || ''
          };
          // Get Firestore image timestamp if available
          if (teacherData.profileImageUrl) {
            firestoreImageTimestamp = teacherData.profileImageUpdatedAt ? teacherData.profileImageUpdatedAt.toMillis() : teacherData.updatedAt ? teacherData.updatedAt.toMillis() : 0;
          }
          // Compare timestamps and use the most recent image
          if (localProfileImage && localStorageImageTimestamp > firestoreImageTimestamp) {
            setProfileImage(localProfileImage);
          } else if (teacherData.profileImageUrl) {
            setProfileImage(teacherData.profileImageUrl);
          }
          setIsLoading(false);
          return;
        }
        // If no teacher document, try to get from users collection
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setFirstName(userData.firstName || '');
          setLastName(userData.lastName || '');
          setEmail(userData.email || '');
          setPhone(userData.phone || '');
          // Store original values for dirty checking
          originalValues.current = {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            phone: userData.phone || '',
            street: '',
            addressLine2: '',
            city: userData.city || '',
            zipCode: '',
            state: '',
            country: ''
          };
          // Get Firestore image timestamp if available
          if (userData.profileImageUrl) {
            firestoreImageTimestamp = userData.profileImageUpdatedAt ? userData.profileImageUpdatedAt.toMillis() : userData.updatedAt ? userData.updatedAt.toMillis() : 0;
          }
          // Compare timestamps and use the most recent image
          if (localProfileImage && localStorageImageTimestamp > firestoreImageTimestamp) {
            setProfileImage(localProfileImage);
          } else if (userData.profileImageUrl) {
            setProfileImage(userData.profileImageUrl);
          }
          // Handle address data
          if (userData.address) {
            setStreet(userData.address.street || '');
            setAddressLine2(userData.address.addressLine2 || '');
            setCity(userData.address.city || '');
            setZipCode(userData.address.zipCode || '');
            setState(userData.address.state || '');
            setCountry(userData.address.country || '');
            // Update original values for address fields
            originalValues.current.street = userData.address.street || '';
            originalValues.current.addressLine2 = userData.address.addressLine2 || '';
            originalValues.current.city = userData.address.city || '';
            originalValues.current.zipCode = userData.address.zipCode || '';
            originalValues.current.state = userData.address.state || '';
            originalValues.current.country = userData.address.country || '';
          } else {
            setCity(userData.city || '');
            originalValues.current.city = userData.city || '';
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };
    fetchUserData();
    // Cleanup function
    return () => {
      isMounted.current = false;
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
      }
      // Cancel any active upload task
      if (activeUploadTask.current) {
        try {
          // If the activeUploadTask has a cancel method, call it
          if (typeof activeUploadTask.current.cancel === 'function') {
            activeUploadTask.current.cancel();
          }
        } catch (error) {
          console.error('Error cancelling upload task:', error);
        }
        activeUploadTask.current = null;
      }
    };
  }, [currentUser]);
  // Auto-sync: When a teacher loads the My Info page, trigger a full sync
  // to ensure publicTeacherProfiles is populated (creates it if missing)
  useEffect(() => {
    if (currentUser?.uid && userType === 'teacher' && !isLoading) {
      fullSyncPublicProfile(currentUser.uid).catch(err => {
        console.warn('[MyInfoPage] Auto full-sync of public profile failed (non-blocking):', err);
      });
    }
  }, [currentUser?.uid, userType, isLoading]);
  // Handle beforeunload event to show browser confirmation dialog
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (formDirty) {
        const message = 'You have unsaved changes. Are you sure you want to leave without saving?';
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formDirty]);
  // Helper function to log detailed error information
  const logDetailedError = (context: string, error: any) => {
    console.error(`[${context}] Error:`, error?.message || 'Unknown error');
  };
  // New function to compress images before upload
  const compressImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        if (!isMounted.current) {
          reject(new Error('Component unmounted during image compression'));
          return;
        }
        setUploadStatus('Compressing image...');
        // Create an image object
        const img = new Image();
        img.onload = () => {
          if (!isMounted.current) {
            URL.revokeObjectURL(img.src);
            reject(new Error('Component unmounted during image compression'));
            return;
          }
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          if (width > IMAGE_COMPRESSION.maxWidth || height > IMAGE_COMPRESSION.maxHeight) {
            if (width > height) {
              // Landscape image
              height = Math.round(height * (IMAGE_COMPRESSION.maxWidth / width));
              width = IMAGE_COMPRESSION.maxWidth;
            } else {
              // Portrait image
              width = Math.round(width * (IMAGE_COMPRESSION.maxHeight / height));
              height = IMAGE_COMPRESSION.maxHeight;
            }
          }
          // Create canvas for resizing
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          // Draw resized image to canvas
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            URL.revokeObjectURL(img.src);
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          // Convert to blob with specified quality
          canvas.toBlob(blob => {
            URL.revokeObjectURL(img.src);
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            if (!isMounted.current) {
              reject(new Error('Component unmounted during image compression'));
              return;
            }
            resolve(blob);
          }, IMAGE_COMPRESSION.format, IMAGE_COMPRESSION.quality);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          reject(new Error('Failed to load image for compression'));
        };
        // Load image from File
        img.src = URL.createObjectURL(file);
      } catch (error) {
        reject(error);
      }
    });
  };
  // Save image to local storage
  const saveImageToLocalStorage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        if (!isMounted.current) {
          reject(new Error('Component unmounted during localStorage save'));
          return;
        }
        setUploadStatus('Saving to local storage...');
        const reader = new FileReader();
        reader.onload = event => {
          if (!isMounted.current) {
            reject(new Error('Component unmounted during localStorage save'));
            return;
          }
          if (!event.target?.result || !currentUser?.uid) {
            reject(new Error('Failed to read file or user not authenticated'));
            return;
          }
          const dataUrl = event.target.result as string;
          // Store in localStorage with timestamp and user's ID
          const timestamp = Date.now();
          const imageData = JSON.stringify({
            imageUrl: dataUrl,
            timestamp: timestamp
          });
          // Store in localStorage with the user's ID to keep it specific to this user
          localStorage.setItem(`${PROFILE_IMAGE_KEY}_${currentUser.uid}`, imageData);
          resolve(dataUrl);
        };
        reader.onerror = () => {
          reject(new Error('Error reading file'));
        };
        reader.readAsDataURL(file);
        // Simulate progress for better UX
        let progress = 0;
        const progressInterval = setInterval(() => {
          if (!isMounted.current) {
            clearInterval(progressInterval);
            return;
          }
          progress += 10;
          if (progress <= 100) {
            setUploadProgress(progress);
          }
          if (progress >= 100 || !isMounted.current) {
            clearInterval(progressInterval);
          }
        }, 100);
      } catch (error) {
        reject(error);
      }
    });
  };
  // Upload image to Firebase Storage with progress tracking and better error handling
  const uploadImageToStorage = async (file: File, folderPath?: string, onProgress?: (progress: number) => void, onStatusChange?: (status: string) => void): Promise<string> => {
    if (!currentUser) {
      const error = new Error('User not authenticated');
      logDetailedError('uploadImageToStorage-auth-check', error);
      throw error;
    }
    // Check if user is still authenticated before starting upload
    if (!currentUser.uid) {
      const error = new Error('Session expired — please sign in again');
      logDetailedError('uploadImageToStorage-uid-check', error);
      throw error;
    }
    // Create a promise that will be resolved by whichever method completes first
    return new Promise(async (resolve, reject) => {
      try {
        // Reset upload progress
        setUploadProgress(0);
        // First compress the image to reduce upload size
        let fileToUpload: File | Blob = file;
        try {
          // Only compress images (not other file types)
          if (file.type.startsWith('image/')) {
            const compressedBlob = await compressImage(file);
            fileToUpload = new File([compressedBlob], file.name, {
              type: IMAGE_COMPRESSION.format,
              lastModified: Date.now()
            });
          }
        } catch (compressError) {
          console.warn('Image compression failed, using original file');
          // Continue with original file if compression fails
        }
        // Check if component is still mounted
        if (!isMounted.current) {
          reject(new Error('Component unmounted before upload started'));
          return;
        }
        // Set a timeout to prevent infinite waiting - 15 seconds timeout (reduced from 30)
        const timeoutId = setTimeout(() => {
          const timeoutError = new Error(`Upload timed out after ${UPLOAD_CONFIG.timeout / 1000} seconds`);
          logDetailedError('uploadImageToStorage-timeout', timeoutError);
          reject(timeoutError);
        }, UPLOAD_CONFIG.timeout);
        uploadTimeoutRef.current = timeoutId as unknown as number;
        // Track which method completed first
        let isResolved = false;
        // Function to handle successful completion from any method
        const completeUpload = (url: string, source: string) => {
          if (isResolved) return; // Prevent resolving multiple times
          isResolved = true;
          clearTimeout(timeoutId);
          uploadTimeoutRef.current = null;
          // Set useLocalStorage flag based on the source
          setUseLocalStorage(source === 'localStorage' || source === 'localStorage-fallback' || source === 'localStorage-sequential');
          resolve(url);
        };
        // Start local storage save in parallel with Firebase if enabled
        if (UPLOAD_CONFIG.useParallelUpload) {
          saveImageToLocalStorage(file).then(localUrl => {
            // Only complete with localStorage if Firebase hasn't already completed
            if (!isResolved && isMounted.current) {
              completeUpload(localUrl, 'localStorage');
            }
          }).catch(error => {
            // Don't reject here, as Firebase upload might still succeed
            console.warn('Local storage save failed:', error);
          });
        }
        // Start Firebase upload
        try {
          if (!isMounted.current) {
            clearTimeout(timeoutId);
            uploadTimeoutRef.current = null;
            reject(new Error('Component unmounted before Firebase upload started'));
            return;
          }
          setUploadStatus('Uploading to cloud...');
          // Create a unique filename to avoid cache issues
          const timestamp = new Date().getTime();
          const filename = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;
          // Create a reference to the storage location
          const storageRef = ref(storage, `profile-images/${currentUser.uid}/${filename}`);
          // Create file metadata including the content type
          const metadata = {
            contentType: fileToUpload.type,
            customMetadata: {
              uploadedBy: currentUser.uid,
              originalFilename: file.name,
              userType: userType || 'unknown',
              timestamp: timestamp.toString()
            }
          };
          // Upload the file with progress tracking
          const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata);
          // Store the upload task for potential cancellation
          activeUploadTask.current = uploadTask;
          // Register three observers:
          uploadTask.on('state_changed', snapshot => {
            // Observe state change events such as progress, pause, and resume
            if (!isMounted.current) return;
            const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
            // Update progress state for UI
            if (isMounted.current) {
              if (onProgress) onProgress(Math.round(progress));
              // Update status message based on progress
              if (onStatusChange) {
                if (progress < 25) {
                  onStatusChange('Starting upload...');
                } else if (progress < 50) {
                  onStatusChange('Uploading to cloud...');
                } else if (progress < 75) {
                  onStatusChange('Almost there...');
                } else {
                  onStatusChange('Finalizing...');
                }
              }
            }
          }, error => {
            // Handle unsuccessful uploads
            activeUploadTask.current = null;
            // Check if error is due to component unmounting
            if (!isMounted.current) {
              clearTimeout(timeoutId);
              uploadTimeoutRef.current = null;
              reject(new Error('Upload canceled - component unmounted'));
              return;
            }
            logDetailedError('uploadImageToStorage-error', error);
            // If Firebase upload fails but we're doing parallel uploads,
            // don't reject yet as local storage might still succeed
            if (!UPLOAD_CONFIG.useParallelUpload) {
              clearTimeout(timeoutId);
              uploadTimeoutRef.current = null;
              reject(error);
            } else {
              // If not already using localStorage, try it now as fallback
              if (!isResolved) {
                saveImageToLocalStorage(file, onProgress, onStatusChange).then(localUrl => {
                  if (isMounted.current) {
                    completeUpload(localUrl, 'localStorage-fallback');
                  }
                }).catch(localError => {
                  // Both methods failed
                  if (!isResolved) {
                    reject(new Error('Failed to upload image using any available method'));
                  }
                });
              }
            }
          }, async () => {
            // Handle successful uploads on complete
            activeUploadTask.current = null;
            if (!isMounted.current) {
              clearTimeout(timeoutId);
              uploadTimeoutRef.current = null;
              return;
            }
            try {
              // Get the download URL
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              completeUpload(downloadURL, 'firebase');
            } catch (error) {
              logDetailedError('uploadImageToStorage-getDownloadURL', error);
              // If we can't get the download URL but we're doing parallel uploads,
              // don't reject yet as local storage might still succeed
              if (!UPLOAD_CONFIG.useParallelUpload) {
                reject(error);
              } else if (!isResolved) {
                // If localStorage hasn't completed yet, wait for it
              }
            }
          });
        } catch (error) {
          logDetailedError('uploadImageToStorage-initialization', error);
          // If Firebase initialization fails but we're doing parallel uploads,
          // don't reject yet as local storage might still succeed
          if (!UPLOAD_CONFIG.useParallelUpload) {
            clearTimeout(timeoutId);
            uploadTimeoutRef.current = null;
            reject(error);
          }
        }
        // If not using parallel uploads, use localStorage as fallback
        if (!UPLOAD_CONFIG.useParallelUpload && !isResolved) {
          try {
            if (isMounted.current) {
              const localUrl = await saveImageToLocalStorage(file, onProgress, onStatusChange);
              completeUpload(localUrl, 'localStorage-sequential');
            }
          } catch (error) {
            if (!isResolved) {
              reject(error);
            }
          }
        }
      } catch (error) {
        logDetailedError('uploadImageToStorage-overall', error);
        reject(error);
      }
    });
  };
  // Delete profile photo handler
  const handleDeleteProfilePhoto = async () => {
    if (!currentUser) return;
    setIsDeletingImage(true);
    setProfileError(null);
    try {
      // Determine which collection to update based on user type
      const collectionName = userType === 'teacher' ? 'teachers' : 'users';
      const userDocRef = doc(db, collectionName, currentUser.uid);
      // Update the document to remove the profile image URL
      await updateDoc(userDocRef, {
        profileImageUrl: null,
        profileImageUpdatedAt: new Date(),
        updatedAt: new Date()
      });
      // Sync banner removal to public profile (non-blocking)
      if (userType === 'teacher' && currentUser?.uid) {
        syncPublicProfile(currentUser.uid, {
          bannerImageUrl: null
        }).catch(err => {
          console.warn('[MyInfoPage] Public profile sync after banner delete failed (non-blocking):', err);
        });
      }
      // Clear the banner preview in state
      setBannerPreviewUrl(null);
      setBannerFile(null);
      setBannerFileName(null);
      // Show success message
      setSaveMessage('Banner image removed successfully!');
      setTimeout(() => {
        if (isMounted.current) {
          setSaveMessage('');
        }
      }, 3000);
    } catch (error) {
      console.error('Error deleting profile photo:', error);
      setProfileError('Failed to remove profile photo. Please try again.');
    } finally {
      if (isMounted.current) {
        setIsDeletingImage(false);
        setShowDeletePhotoConfirm(false);
      }
    }
  };
  // Handle clicking the profile photo or edit button
  const handleProfilePhotoClick = () => {
    if (profileImage) {
      // If there's already a photo, show the menu
      setShowPhotoMenu(true);
    } else {
      // If no photo, directly open file picker
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };
  // Handle "Replace Photo" from menu
  const handleReplacePhoto = () => {
    setShowPhotoMenu(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Handle "Remove Photo" from menu
  const handleRemovePhotoFromMenu = () => {
    setShowPhotoMenu(false);
    setShowDeletePhotoConfirm(true);
  };
  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset previous error
    setProfileError(null);
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      const error = `Invalid file type: ${file.type}. Please select a JPG, PNG, or WebP image`;
      setProfileError(error);
      return;
    }
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      const error = 'Your photo size is bigger than allowed (max 2MB).';
      setProfileError(error);
      return;
    }
    // Check if user is still authenticated
    if (!currentUser) {
      const error = 'Session expired — please sign in again';
      setProfileError(error);
      return;
    }
    console.log('[Profile Upload] Starting Firebase-only upload for user:', currentUser.uid);
    console.log('[Profile Upload] File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    // Create a temporary URL for preview
    const tempImageUrl = URL.createObjectURL(file);
    try {
      // Show loading state
      setIsUploadingImage(true);
      setUploadProgress(0);
      setUploadStatus('Preparing upload...');
      // Show the temporary image while uploading
      setProfileImage(tempImageUrl);
      // Create unique filename
      const timestamp = new Date().getTime();
      const filename = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;
      const storagePath = `profile-images/${currentUser.uid}/${filename}`;
      console.log('[Profile Upload] Storage path:', storagePath);
      // Create storage reference
      const storageRef = ref(storage, storagePath);
      // Create file metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: currentUser.uid,
          originalFilename: file.name,
          userType: userType || 'unknown',
          timestamp: timestamp.toString()
        }
      };
      console.log('[Profile Upload] Starting Firebase Storage upload...');
      // Upload the file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      // Wait for upload to complete
      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed', snapshot => {
          const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
          console.log('[Profile Upload] Progress:', Math.round(progress) + '%');
          setUploadProgress(Math.round(progress));
          // Update status based on progress
          if (progress < 25) {
            setUploadStatus('Starting upload...');
          } else if (progress < 50) {
            setUploadStatus('Uploading...');
          } else if (progress < 75) {
            setUploadStatus('Almost there...');
          } else {
            setUploadStatus('Finalizing...');
          }
        }, error => {
          console.error('[Profile Upload] Firebase upload error:', error);
          console.error('[Profile Upload] Error code:', error.code);
          console.error('[Profile Upload] Error message:', error.message);
          // Provide helpful error message
          if (error.code === 'storage/unauthorized') {
            reject(new Error('Upload failed: Permission denied. Please check Firebase Storage rules.'));
          } else if (error.message.includes('CORS')) {
            reject(new Error('Upload failed: CORS error. Firebase Storage needs to be configured to allow uploads from this domain.'));
          } else {
            reject(error);
          }
        }, () => {
          console.log('[Profile Upload] Upload complete');
          resolve();
        });
      });
      // Get the download URL
      console.log('[Profile Upload] Getting download URL...');
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      console.log('[Profile Upload] Download URL received:', downloadURL);
      // Validate URL format
      if (!downloadURL.startsWith('https://firebasestorage.googleapis.com')) {
        throw new Error('Invalid URL format received from Firebase Storage');
      }
      // Check if component is still mounted
      if (!isMounted.current) {
        console.log('[Profile Upload] Component unmounted, aborting');
        URL.revokeObjectURL(tempImageUrl);
        return;
      }
      // Update Firestore with the profile image URL
      const collectionName = userType === 'teacher' ? 'teachers' : 'users';
      const userDocRef = doc(db, collectionName, currentUser.uid);
      const firestoreTimestamp = new Date();
      console.log('[Profile Upload] Updating Firestore:', collectionName, currentUser.uid);
      console.log('[Profile Upload] Saving URL:', downloadURL);
      await updateDoc(userDocRef, {
        profileImageUrl: downloadURL,
        profileImageUpdatedAt: firestoreTimestamp,
        updatedAt: firestoreTimestamp
      });
      console.log('[Profile Upload] Firestore update successful');
      // Sync profile image to public profile (non-blocking)
      if (userType === 'teacher' && currentUser?.uid) {
        syncPublicProfile(currentUser.uid, {
          profileImageUrl: downloadURL
        }).catch(err => {
          console.warn('[MyInfoPage] Public profile sync after photo upload failed (non-blocking):', err);
        });
      }
      // Clean up localStorage - we don't need it anymore
      if (currentUser.uid) {
        localStorage.removeItem(`${PROFILE_IMAGE_KEY}_${currentUser.uid}`);
      }
      // Update the state with the permanent URL
      setProfileImage(downloadURL);
      setUseLocalStorage(false);
      // Show success message
      setSaveMessage('Profile photo updated successfully!');
      setTimeout(() => {
        if (isMounted.current) {
          setSaveMessage('');
        }
      }, 3000);
      console.log('[Profile Upload] Upload complete!');
    } catch (error) {
      console.error('[Profile Upload] Error:', error);
      // User-friendly error message
      let userMessage = 'Failed to upload profile photo. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('CORS')) {
          userMessage = 'Upload failed due to server configuration. Please contact support.';
        } else if (error.message.includes('Permission denied')) {
          userMessage = 'Upload failed: Permission denied. Please contact support.';
        } else if (error.message.includes('Invalid URL')) {
          userMessage = 'Upload failed: Invalid response from server.';
        } else {
          userMessage = error.message;
        }
      }
      console.error('[Profile Upload] User-facing error:', userMessage);
      setProfileError(userMessage);
      // Revert to previous profile image if there was one
      if (isMounted.current) {
        const collectionName = userType === 'teacher' ? 'teachers' : 'users';
        const userDocRef = doc(db, collectionName, currentUser.uid);
        getDoc(userDocRef).then(docSnap => {
          if (docSnap.exists() && docSnap.data().profileImageUrl) {
            const previousUrl = docSnap.data().profileImageUrl;
            if (previousUrl && previousUrl.startsWith('https://firebasestorage.googleapis.com')) {
              console.log('[Profile Upload] Reverting to previous profile image');
              setProfileImage(previousUrl);
            } else {
              console.log('[Profile Upload] Previous profile URL invalid, clearing');
              setProfileImage(null);
            }
          } else {
            console.log('[Profile Upload] No previous profile image to revert to');
            setProfileImage(null);
          }
        }).catch(revertError => {
          console.error('[Profile Upload] Error reverting profile image:', revertError);
          setProfileImage(null);
        });
      }
    } finally {
      // Release the object URL to avoid memory leaks
      URL.revokeObjectURL(tempImageUrl);
      if (isMounted.current) {
        setIsUploadingImage(false);
        setUploadStatus('');
        setUploadProgress(0);
      }
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  // Validate phone number - Updated for international formats
  const validatePhone = (value: string): boolean => {
    // Allow empty values (not required)
    if (!value) return true;
    // International format: starts with + followed by 8-15 digits
    // Ignore spaces, hyphens, and parentheses when counting digits
    const cleanedNumber = value.replace(/[\s\-\(\)]/g, '');
    // Check if it starts with + and has between 8-15 digits after the +
    const intlPhoneRegex = /^\+\d{8,15}$/;
    // Also accept North American format as a fallback
    const naPhoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    return intlPhoneRegex.test(cleanedNumber) || naPhoneRegex.test(value);
  };
  // Validate postal/zip code
  const validateZipCode = (value: string): boolean => {
    // Allow empty values (not required)
    if (!value) return true;
    // US ZIP code (5 digits or ZIP+4)
    const usZipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    // Canadian postal code (A1A 1A1 format)
    const canadianPostalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    // UK postal code (complex format)
    const ukPostalRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
    return usZipRegex.test(value) || canadianPostalRegex.test(value) || ukPostalRegex.test(value);
  };
  // Handle phone input change with validation
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);
    if (value && !validatePhone(value)) {
      setPhoneError('Please enter a valid phone number');
    } else {
      setPhoneError(null);
    }
  };
  // Handle zip code input change with validation
  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setZipCode(value);
    if (value && !validateZipCode(value)) {
      setZipCodeError('Please enter a valid postal/ZIP code');
    } else {
      setZipCodeError(null);
    }
  };
  // Handle edit mode toggle with unsaved changes check
  const handleEditModeToggle = field => {
    if (formDirty) {
      lastFocusedElement.current = document.activeElement;
      setShowDiscardDialog(true);
      setPendingNavigation(() => () => {
        if (field === 'personal') {
          setPersonalInfoEditMode(!personalInfoEditMode);
        } else if (field === 'address') {
          setAddressEditMode(!addressEditMode);
        }
        // Reset form values to original values
        if (personalInfoEditMode || addressEditMode) {
          setFirstName(originalValues.current.firstName);
          setLastName(originalValues.current.lastName);
          setEmail(originalValues.current.email);
          setPhone(originalValues.current.phone);
          setStreet(originalValues.current.street);
          setAddressLine2(originalValues.current.addressLine2);
          setCity(originalValues.current.city);
          setZipCode(originalValues.current.zipCode);
          setState(originalValues.current.state);
          setCountry(originalValues.current.country);
        }
      });
      return;
    }
    if (field === 'personal') {
      setPersonalInfoEditMode(!personalInfoEditMode);
    } else if (field === 'address') {
      setAddressEditMode(!addressEditMode);
    }
  };
  // Handle form submission
  const handleSubmit = async () => {
    if (!currentUser) return;
    // Validate fields before submission
    let hasErrors = false;
    if (phone && !validatePhone(phone)) {
      setPhoneError('Please enter a valid phone number');
      hasErrors = true;
    }
    if (zipCode && !validateZipCode(zipCode)) {
      setZipCodeError('Please enter a valid postal/ZIP code');
      hasErrors = true;
    }
    if (hasErrors) {
      return;
    }
    setIsSaving(true);
    setSaveMessage('');
    try {
      // Prepare data to update
      let updateData: any = {};
      if (personalInfoEditMode) {
        updateData.firstName = firstName;
        updateData.lastName = lastName;
        updateData.email = email;
        updateData.phone = phone;
        updateData.displayName = `${firstName} ${lastName}`.trim();
      }
      if (addressEditMode) {
        // For teachers, we need to update both nested and top-level address fields
        if (userType === 'teacher') {
          // Update top-level address fields for backward compatibility
          updateData.street = street;
          updateData.addressLine2 = addressLine2;
          updateData.city = city;
          updateData.zipCode = zipCode;
          updateData.state = state;
          updateData.country = country;
          // Also include the nested address object for consistency
          updateData.address = {
            street,
            addressLine2,
            city,
            zipCode,
            state,
            country
          };
        } else {
          // For users, just update the address object
          updateData.address = {
            street,
            addressLine2,
            city,
            zipCode,
            state,
            country
          };
        }
      }
      // Use updateUserProfile from AuthContext to update both collections
      await updateUserProfile(updateData);
      // Sync public-facing fields to publicTeacherProfiles (non-blocking)
      // This ensures the shared Teacher Page link stays up-to-date
      if (userType === 'teacher' && currentUser?.uid) {
        syncPublicProfile(currentUser.uid, updateData).catch(err => {
          console.warn('[MyInfoPage] Public profile sync failed (non-blocking):', err);
        });
      }
      // Update original values after successful save
      if (personalInfoEditMode) {
        originalValues.current.firstName = firstName;
        originalValues.current.lastName = lastName;
        originalValues.current.email = email;
        originalValues.current.phone = phone;
      }
      if (addressEditMode) {
        originalValues.current.street = street;
        originalValues.current.addressLine2 = addressLine2;
        originalValues.current.city = city;
        originalValues.current.zipCode = zipCode;
        originalValues.current.state = state;
        originalValues.current.country = country;
      }
      // Exit edit modes
      setPersonalInfoEditMode(false);
      setAddressEditMode(false);
      // Reset form dirty state
      setFormDirty(false);
      // Show success message
      setSaveMessage('Your information has been updated successfully!');
      // Clear success message after 3 seconds
      setTimeout(() => {
        if (isMounted.current) {
          setSaveMessage('');
        }
      }, 3000);
    } catch (error) {
      console.error('Error updating user data:', error);
      setSaveMessage('Failed to update information. Please try again.');
    } finally {
      if (isMounted.current) {
        setIsSaving(false);
      }
    }
  };
  // Handle discard dialog confirmation
  const handleDiscardChanges = () => {
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    setShowDiscardDialog(false);
  };
  // Handle discard dialog cancellation
  const handleKeepChanges = () => {
    setShowDiscardDialog(false);
    setPendingNavigation(null);
    // Return focus to the last focused element
    if (lastFocusedElement.current) {
      ;
      (lastFocusedElement.current as HTMLElement).focus();
    }
  };
  // Banner upload handlers
  const handleBannerClick = () => {
    if (bannerPreviewUrl) {
      // If there's already a banner, show the menu
      setShowBannerMenu(true);
    } else {
      // If no banner, directly open file picker
      if (bannerFileInputRef.current) {
        bannerFileInputRef.current.click();
      }
    }
  };
  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerError(null);
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setBannerError(`Invalid file type. Please select a JPG, PNG, or WebP image.`);
      return;
    }
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setBannerError('Your banner image is bigger than allowed (max 5MB).');
      return;
    }
    // Store file in state
    setBannerFile(file);
    setBannerFileName(file.name);
    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    setBannerPreviewUrl(tempPreviewUrl);
    // Upload to Firebase immediately and pass the temp URL for cleanup
    uploadBannerToFirebase(file, tempPreviewUrl);
  };
  // Handle "Replace Banner" from menu
  const handleReplaceBanner = () => {
    setShowBannerMenu(false);
    if (bannerFileInputRef.current) {
      bannerFileInputRef.current.click();
    }
  };
  // Handle "Remove Banner" from menu
  const handleRemoveBannerFromMenu = () => {
    setShowBannerMenu(false);
    setShowDeleteBannerConfirm(true);
  };
  // Upload banner image to Firebase Storage - FIREBASE ONLY, NO FALLBACK
  const uploadBannerToFirebase = async (file: File, tempPreviewUrl: string) => {
    if (!currentUser) {
      setBannerError('User not authenticated');
      setIsUploadingBanner(false);
      return;
    }
    console.log('[Banner Upload] Starting Firebase-only upload for user:', currentUser.uid);
    console.log('[Banner Upload] File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    setIsUploadingBanner(true);
    setBannerUploadProgress(0);
    setBannerError(null);
    try {
      // Create unique filename
      const timestamp = new Date().getTime();
      const filename = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;
      const storagePath = `banner-images/${currentUser.uid}/${filename}`;
      console.log('[Banner Upload] Storage path:', storagePath);
      // Create storage reference
      const storageRef = ref(storage, storagePath);
      // Create file metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: currentUser.uid,
          originalFilename: file.name,
          userType: userType || 'unknown',
          timestamp: timestamp.toString()
        }
      };
      console.log('[Banner Upload] Starting Firebase Storage upload (no fallback)...');
      // Upload the file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      // Wait for upload to complete
      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed', snapshot => {
          const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
          console.log('[Banner Upload] Progress:', Math.round(progress) + '%');
          setBannerUploadProgress(Math.round(progress));
        }, error => {
          console.error('[Banner Upload] Firebase upload error:', error);
          console.error('[Banner Upload] Error code:', error.code);
          console.error('[Banner Upload] Error message:', error.message);
          // Provide helpful error message based on error type
          if (error.code === 'storage/unauthorized') {
            reject(new Error('Upload failed: Permission denied. Please check Firebase Storage rules.'));
          } else if (error.message.includes('CORS')) {
            reject(new Error('Upload failed: CORS error. Firebase Storage needs to be configured to allow uploads from this domain.'));
          } else {
            reject(error);
          }
        }, () => {
          console.log('[Banner Upload] Upload complete');
          resolve();
        });
      });
      // Get the download URL
      console.log('[Banner Upload] Getting download URL...');
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      console.log('[Banner Upload] Download URL received:', downloadURL);
      // STRICT VALIDATION: Only accept Firebase Storage URLs
      if (!downloadURL.startsWith('https://firebasestorage.googleapis.com')) {
        const errorMsg = `Invalid URL format: Expected Firebase Storage URL, got ${downloadURL.substring(0, 50)}...`;
        console.error('[Banner Upload]', errorMsg);
        throw new Error('Upload failed: Invalid URL format received from Firebase Storage');
      }
      // Additional validation: reject base64 and blob URLs
      if (downloadURL.startsWith('data:') || downloadURL.startsWith('blob:')) {
        console.error('[Banner Upload] Received invalid URL type:', downloadURL.substring(0, 50));
        throw new Error('Upload failed: Invalid URL type (expected Firebase Storage URL)');
      }
      console.log('[Banner Upload] URL validation passed');
      // Check if component is still mounted
      if (!isMounted.current) {
        console.log('[Banner Upload] Component unmounted, aborting');
        return;
      }
      // Update Firestore with the banner URL
      const collectionName = userType === 'teacher' ? 'teachers' : 'users';
      const userDocRef = doc(db, collectionName, currentUser.uid);
      const firestoreTimestamp = new Date();
      console.log('[Banner Upload] Updating Firestore:', collectionName, currentUser.uid);
      console.log('[Banner Upload] Saving URL:', downloadURL);
      await updateDoc(userDocRef, {
        bannerImageUrl: downloadURL,
        bannerImageUpdatedAt: firestoreTimestamp,
        updatedAt: firestoreTimestamp
      });
      console.log('[Banner Upload] Firestore update successful');
      // Sync banner image to public profile (non-blocking)
      if (userType === 'teacher' && currentUser?.uid) {
        syncPublicProfile(currentUser.uid, {
          bannerImageUrl: downloadURL
        }).catch(err => {
          console.warn('[MyInfoPage] Public profile sync after banner upload failed (non-blocking):', err);
        });
      }
      // Clean up the temporary blob URL
      URL.revokeObjectURL(tempPreviewUrl);
      console.log('[Banner Upload] Cleaned up temporary preview URL');
      // Update preview URL to use the permanent Firebase URL
      setBannerPreviewUrl(downloadURL);
      // Reset upload state
      setIsUploadingBanner(false);
      setBannerUploadProgress(0);
      // Show success message
      setSaveMessage('Banner image updated successfully!');
      setTimeout(() => {
        if (isMounted.current) {
          setSaveMessage('');
        }
      }, 3000);
      console.log('[Banner Upload] Upload complete!');
    } catch (error) {
      console.error('[Banner Upload] Error:', error);
      // Clean up the temporary blob URL on error
      URL.revokeObjectURL(tempPreviewUrl);
      // User-friendly error message
      let userMessage = 'Failed to upload banner. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('CORS')) {
          userMessage = 'Upload failed due to server configuration. Please contact support.';
        } else if (error.message.includes('Permission denied')) {
          userMessage = 'Upload failed: Permission denied. Please contact support.';
        } else if (error.message.includes('Invalid URL')) {
          userMessage = 'Upload failed: Invalid response from server.';
        } else {
          userMessage = error.message;
        }
      }
      console.error('[Banner Upload] User-facing error:', userMessage);
      setBannerError(userMessage);
      // Revert to previous banner image if there was one
      if (isMounted.current) {
        const collectionName = userType === 'teacher' ? 'teachers' : 'users';
        const userDocRef = doc(db, collectionName, currentUser.uid);
        getDoc(userDocRef).then(docSnap => {
          if (docSnap.exists() && docSnap.data().bannerImageUrl) {
            const previousUrl = docSnap.data().bannerImageUrl;
            // Only revert if it's a valid Firebase URL
            if (previousUrl && previousUrl.startsWith('https://firebasestorage.googleapis.com')) {
              console.log('[Banner Upload] Reverting to previous banner');
              setBannerPreviewUrl(previousUrl);
            } else {
              console.log('[Banner Upload] Previous banner URL invalid, clearing');
              setBannerPreviewUrl(null);
            }
          } else {
            console.log('[Banner Upload] No previous banner to revert to');
            setBannerPreviewUrl(null);
          }
        }).catch(revertError => {
          console.error('[Banner Upload] Error reverting banner:', revertError);
        });
      }
      // Always reset upload state on error
      setIsUploadingBanner(false);
      setBannerUploadProgress(0);
    }
  };
  // Delete banner handler
  const handleDeleteBanner = async () => {
    if (!currentUser) return;
    setIsDeletingBanner(true);
    setBannerError(null);
    try {
      // Determine which collection to update based on user type
      const collectionName = userType === 'teacher' ? 'teachers' : 'users';
      const userDocRef = doc(db, collectionName, currentUser.uid);
      // Update the document to remove the banner image URL
      await updateDoc(userDocRef, {
        bannerImageUrl: null,
        bannerImageUpdatedAt: new Date(),
        updatedAt: new Date()
      });
      // Sync banner removal to public profile (non-blocking)
      if (userType === 'teacher' && currentUser?.uid) {
        syncPublicProfile(currentUser.uid, {
          bannerImageUrl: null
        }).catch(err => {
          console.warn('[MyInfoPage] Public profile sync after banner delete failed (non-blocking):', err);
        });
      }
      // Clear the banner preview in state
      setBannerPreviewUrl(null);
      setBannerFile(null);
      setBannerFileName(null);
      // Show success message
      setSaveMessage('Banner image removed successfully!');
      setTimeout(() => {
        if (isMounted.current) {
          setSaveMessage('');
        }
      }, 3000);
    } catch (error) {
      console.error('Error deleting banner:', error);
      setBannerError('Failed to remove banner. Please try again.');
    } finally {
      if (isMounted.current) {
        setIsDeletingBanner(false);
      }
    }
  };
  const handleBannerDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingBanner(true);
  };
  const handleBannerDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingBanner(false);
  };
  const handleBannerDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingBanner(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setBannerError(null);
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setBannerError(`Invalid file type. Please select a JPG, PNG, or WebP image.`);
      return;
    }
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setBannerError('Your banner image is bigger than allowed (max 5MB).');
      return;
    }
    // Store file in state
    setBannerFile(file);
    setBannerFileName(file.name);
    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    setBannerPreviewUrl(tempPreviewUrl);
    // Upload to Firebase immediately and pass the temp URL for cleanup
    uploadBannerToFirebase(file, tempPreviewUrl);
  };
  // Profile photo drag handlers
  const handleProfilePhotoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!profileImage) {
      setIsDraggingProfilePhoto(true);
    }
  };
  const handleProfilePhotoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingProfilePhoto(false);
  };
  const handleProfilePhotoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingProfilePhoto(false);
    if (profileImage) return; // Only allow drop when no photo exists
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // Reset previous error
    setProfileError(null);
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      const error = `Invalid file type: ${file.type}. Please select a JPG, PNG, or WebP image`;
      setProfileError(error);
      return;
    }
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      const error = 'Your photo size is bigger than allowed (max 2MB).';
      setProfileError(error);
      return;
    }
    // Trigger the same upload flow as file input
    const syntheticEvent = {
      target: {
        files: [file]
      }
    } as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(syntheticEvent);
  };
  // Render loading skeletons for initial data load
  const renderLoadingSkeletons = () => <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8" data-id="element-5446">
        <div className="flex items-center mb-4 md:mb-0" data-id="element-5447">
          <div className={`p-3 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} mr-4 animate-pulse`} data-id="element-5448">
            <div className="h-8 w-8 rounded-full bg-gray-500" data-id="element-5449"></div>
          </div>
          <div data-id="element-5450">
            <div className={`h-8 w-48 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded animate-pulse mb-2`} data-id="element-5451"></div>
            <div className={`h-5 w-64 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded animate-pulse`} data-id="element-5452"></div>
          </div>
        </div>
        <div className={`h-10 w-32 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg animate-pulse`} data-id="element-5453"></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5454">
        {/* Profile Photo Skeleton */}
        <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1 h-fit`} data-id="element-5455">
          <div className={`h-6 w-32 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded animate-pulse mb-6`} data-id="element-5456"></div>
          <div className="flex flex-col items-center" data-id="element-5457">
            <div className={`h-40 w-40 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse mb-4`} data-id="element-5458"></div>
            <div className={`h-9 w-32 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg animate-pulse`} data-id="element-5459"></div>
          </div>
        </div>
        {/* Personal Information Skeleton */}
        <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2 relative`} data-id="element-5460">
          <div className="flex justify-between items-center mb-6" data-id="element-5461">
            <div className={`h-6 w-48 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded animate-pulse`} data-id="element-5462"></div>
            <div className={`h-8 w-16 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-md animate-pulse`} data-id="element-5463"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5464">
            {[1, 2, 3, 4].map(i => <div key={i} data-id="element-5465">
                <div className={`h-4 w-24 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded animate-pulse mb-2`} data-id="element-5466"></div>
                <div className={`h-10 w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg animate-pulse`} data-id="element-5467"></div>
              </div>)}
          </div>
        </div>
      </div>
    </>;
  return <main className="pb-6" data-id="element-5468">
      <div className="mx-auto px-4" data-id="element-5469">
        {isLoading ? renderLoadingSkeletons() : <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-5470">
              <h1 className="text-2xl sm:text-3xl font-bold" data-id="element-5471">My Info</h1>
            </div>
            {saveMessage && <div className={`mb-6 p-4 rounded-lg ${saveMessage.includes('Failed') ? darkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700' : darkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'} flex items-center`} data-id="element-5472">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-5473" />
                {saveMessage}
              </div>}
            {useLocalStorage && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'} flex items-start`} data-id="element-5474">
                <InfoIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-5475" />
                <div data-id="element-5476">
                  <p className="font-medium" data-id="element-5477">
                    Using local storage for profile images
                  </p>
                  <p className="text-sm mt-1" data-id="element-5478">
                    Your profile image is stored in your browser. It will be
                    visible on this device but may not sync across devices.
                  </p>
                </div>
              </div>}

            {/* Banner Upload Section */}
            <div className="mb-6 -mx-4 md:mx-0" data-id="element-5479">
              <div className="relative" data-id="element-5480">
                <div onClick={handleBannerClick} onDragOver={handleBannerDragOver} onDragLeave={handleBannerDragLeave} onDrop={handleBannerDrop} className={`relative h-32 md:h-48 rounded-lg md:rounded-lg overflow-hidden group ${bannerPreviewUrl ? 'cursor-pointer transition-all duration-200 hover:brightness-110' : `border-2 border-dashed ${isDraggingBanner ? 'border-red-500 bg-red-500/10' : darkMode ? 'border-gray-600 bg-navy-900/30' : 'border-gray-300 bg-gray-50'} cursor-pointer transition-all duration-200 hover:border-red-500`} flex items-center justify-center mx-2 md:mx-0`} data-id="element-5481">
                  {bannerPreviewUrl ? <>
                      <img src={bannerPreviewUrl} alt="Banner preview" className="absolute inset-0 w-full h-full object-cover object-[center_35%] md:object-center" data-id="element-5482" />

                      {/* Edit Overlay - visible on hover (desktop) or always (mobile) */}
                      {!isUploadingBanner && !isDeletingBanner && <div className="absolute inset-0 bg-black/40 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-2" data-id="element-5483">
                          <Camera className="w-8 h-8 text-white" strokeWidth={1.5} data-id="element-5484" />
                          <span className="text-white text-sm font-medium" data-id="element-5485">
                            Change banner
                          </span>
                        </div>}

                      {isUploadingBanner && <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50" data-id="element-5486">
                          <LoaderIcon className="h-10 w-10 text-white animate-spin mb-2" data-id="element-5487" />
                          <span className="text-white text-sm font-medium" data-id="element-5488">
                            Uploading... {bannerUploadProgress}%
                          </span>
                        </div>}
                      {isDeletingBanner && <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50" data-id="element-5489">
                          <LoaderIcon className="h-10 w-10 text-white animate-spin mb-2" data-id="element-5490" />
                          <span className="text-white text-sm font-medium" data-id="element-5491">
                            Removing...
                          </span>
                        </div>}
                    </> : <div className="text-center" data-id="element-5492">
                      <UploadIcon className={`h-10 w-10 mx-auto mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5493" />
                      <p className={`text-base font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5494">
                        Click to upload or drag & drop
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5495">
                        JPG, PNG, WebP (max 5MB)
                      </p>
                    </div>}
                  {bannerError && <div className="absolute bottom-2 left-0 right-0 text-center" data-id="element-5496">
                      <p className="text-sm text-red-500 bg-white dark:bg-navy-800 px-3 py-1 rounded inline-block" data-id="element-5497">
                        {bannerError}
                      </p>
                    </div>}
                  <input type="file" ref={bannerFileInputRef} onChange={handleBannerFileChange} accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" aria-hidden="true" data-id="element-5498" />
                </div>

                {/* Edit Banner Menu - positioned at top-right of banner */}
                {showBannerMenu && bannerPreviewUrl && <>
                    {/* Backdrop to close menu */}
                    <div className="fixed inset-0 z-40" onClick={() => setShowBannerMenu(false)} data-id="element-5499" />
                    {/* Menu - anchored to top-right corner of banner */}
                    <div className={`absolute top-2 right-2 w-44 rounded-lg shadow-lg z-50 ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-5500">
                      <div className="py-1" data-id="element-5501">
                        <button onClick={handleReplaceBanner} className={`w-full text-left px-4 py-3 text-sm flex items-center ${darkMode ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-5502">
                          <UploadIcon className="h-4 w-4 mr-3" data-id="element-5503" />
                          Replace Banner
                        </button>
                        <button onClick={handleRemoveBannerFromMenu} className={`w-full text-left px-4 py-3 text-sm flex items-center text-red-500 ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} transition-colors`} data-id="element-5504">
                          <Trash2Icon className="h-4 w-4 mr-3" data-id="element-5505" />
                          Remove Banner
                        </button>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5506">
              {/* Profile Photo Section */}
              <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1 h-fit`} data-id="element-5507">
                <h2 className="text-xl font-serif font-semibold mb-4" data-id="element-5508">
                  Profile Photo
                </h2>
                <div className="flex flex-col items-center" data-id="element-5509">
                  <div className="relative" data-id="element-5510">
                    {/* The profile photo container - now clickable */}
                    <div onClick={handleProfilePhotoClick} onDragOver={handleProfilePhotoDragOver} onDragLeave={handleProfilePhotoDragLeave} onDrop={handleProfilePhotoDrop} className={`h-40 w-40 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden mb-4 cursor-pointer relative transition-all duration-200 group ${profileImage && !isUploadingImage && !isDeletingImage ? 'hover:brightness-110 hover:ring-2 hover:ring-deep-pink hover:ring-opacity-50' : !profileImage && !isUploadingImage && !isDeletingImage ? `hover:ring-2 hover:ring-red-500 ${isDraggingProfilePhoto ? 'ring-2 ring-red-500' : ''}` : ''}`} role="button" tabIndex={0} aria-label="Change profile photo" onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleProfilePhotoClick();
                  }
                }} data-id="element-5511">
                      {isUploadingImage ? <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 rounded-full" data-id="element-5512">
                          <LoaderIcon className="h-10 w-10 text-white animate-spin" data-id="element-5513" />
                          <span className="text-white text-xs mt-2" data-id="element-5514">
                            {uploadStatus || 'Uploading...'}
                          </span>
                          {/* Display upload percentage */}
                          <span className="text-white font-bold mt-1" data-id="element-5515">
                            {uploadProgress}%
                          </span>
                        </div> : isDeletingImage ? <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 rounded-full" data-id="element-5516">
                          <LoaderIcon className="h-10 w-10 text-white animate-spin" data-id="element-5517" />
                          <span className="text-white text-xs mt-2" data-id="element-5518">
                            Removing...
                          </span>
                        </div> : profileImage ? <>
                          <img src={profileImage} alt="Profile" className="h-full w-full object-cover" data-id="element-5519" />
                          {/* Edit Overlay - visible on hover (desktop) or always (mobile) */}
                          <div className="absolute inset-0 bg-black/40 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-1 rounded-full" data-id="element-5520">
                            <Camera className="w-7 h-7 text-white" strokeWidth={1.5} data-id="element-5521" />
                            <span className="text-white text-xs font-medium" data-id="element-5522">
                              Change photo
                            </span>
                          </div>
                        </> : <>
                          <UserIcon className={`h-20 w-20 ${darkMode ? 'text-gray-600' : 'text-gray-400'} ${isDraggingProfilePhoto ? 'opacity-30' : ''}`} data-id="element-5523" />
                          {isDraggingProfilePhoto && <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 rounded-full" data-id="element-5524">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-200" data-id="element-5525">
                                Drop to upload
                              </span>
                            </div>}
                        </>}
                    </div>

                    {/* Edit Photo Menu */}
                    {showPhotoMenu && profileImage && <>
                        {/* Backdrop to close menu */}
                        <div className="fixed inset-0 z-40" onClick={() => setShowPhotoMenu(false)} data-id="element-5526" />
                        {/* Menu - positioned directly under avatar, centered */}
                        <div className={`absolute left-1/2 transform -translate-x-1/2 top-full mt-2 w-44 rounded-lg shadow-lg z-50 ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-5527">
                          <div className="py-1" data-id="element-5528">
                            <button onClick={handleReplacePhoto} className={`w-full text-left px-4 py-3 text-sm flex items-center ${darkMode ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-5529">
                              <UploadIcon className="h-4 w-4 mr-3" data-id="element-5530" />
                              Replace Photo
                            </button>
                            <button onClick={handleRemovePhotoFromMenu} className={`w-full text-left px-4 py-3 text-sm flex items-center text-red-500 ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} transition-colors`} data-id="element-5531">
                              <Trash2Icon className="h-4 w-4 mr-3" data-id="element-5532" />
                              Remove Photo
                            </button>
                          </div>
                        </div>
                      </>}

                    {/* Progress bar */}
                    {isUploadingImage && <div className="w-40 h-2 bg-gray-200 rounded-full overflow-hidden mt-1 mb-3" data-id="element-5533">
                        <div className="h-full bg-blue-500 transition-all duration-300 ease-out" style={{
                    width: `${uploadProgress}%`
                  }} data-id="element-5534"></div>
                      </div>}
                    {/* Hidden file input */}
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" aria-hidden="true" disabled={isUploadingImage || isDeletingImage} data-id="element-5535" />
                  </div>
                  {/* Error message */}
                  {profileError && <div className="text-center" data-id="element-5536">
                      <p className="text-red-500 text-sm mt-2 mb-2 flex items-center justify-center" data-id="element-5537">
                        <AlertCircleIcon className="h-4 w-4 mr-1" data-id="element-5538" />
                        {profileError}
                      </p>
                    </div>}
                  {/* File type info */}
                  <p className={`text-xs text-center mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5539">
                    Accepted formats: JPG, PNG, WebP. Max size: 2MB.
                  </p>
                </div>
              </div>
              {/* Personal Information Section */}
              <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2 relative`} data-id="element-5540">
                <div className="flex justify-between items-center mb-4" data-id="element-5541">
                  <h2 className="text-xl font-serif font-semibold" data-id="element-5542">
                    Personal Information
                  </h2>
                  <div className="flex gap-2" data-id="element-5543">
                    {personalInfoEditMode && <button onClick={handleSubmit} disabled={isSaving || !!phoneError} className="px-3 py-1 rounded-md bg-green-500 text-white hover:bg-green-600 text-sm flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5544">
                        {isSaving ? <>
                            <LoaderIcon className="h-3.5 w-3.5 mr-1 animate-spin" data-id="element-5545" />
                            Saving...
                          </> : <>
                            <SaveIcon className="h-3.5 w-3.5 mr-1" data-id="element-5546" />
                            Save Changes
                          </>}
                      </button>}
                    <button onClick={() => handleEditModeToggle('personal')} className="px-3 py-1 rounded-md bg-transparent border border-green-500 text-green-500 hover:bg-green-500 hover:text-white text-sm flex items-center transition-colors" data-id="element-5547">
                      {personalInfoEditMode ? <>
                          <XIcon className="h-3.5 w-3.5 mr-1" data-id="element-5548" />
                          Cancel
                        </> : <>
                          <EditIcon className="h-3.5 w-3.5 mr-1" data-id="element-5549" />
                          Edit
                        </>}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5550">
                  {/* First Name */}
                  <div data-id="element-5551">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5552">
                      First Name
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} disabled={!personalInfoEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!personalInfoEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="Your first name" data-id="element-5553" />
                  </div>
                  {/* Last Name */}
                  <div data-id="element-5554">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5555">
                      Last Name
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} disabled={!personalInfoEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!personalInfoEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="Your last name" data-id="element-5556" />
                  </div>
                  {/* Email Address */}
                  <div data-id="element-5557">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5558">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-5559">
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={!personalInfoEditMode} className={`w-full pl-10 pr-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!personalInfoEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="Your email address" data-id="element-5560" />
                      <MailIcon className="absolute left-3 top-2.5 h-5 w-5 text-blue-500" data-id="element-5561" />
                    </div>
                  </div>
                  {/* Phone Number */}
                  <div data-id="element-5562">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5563">
                      Phone Number
                    </label>
                    <div className="relative" data-id="element-5564">
                      <input type="tel" value={phone} onChange={handlePhoneChange} disabled={!personalInfoEditMode} className={`w-full pl-10 pr-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${phoneError ? 'border-red-500' : ''} ${!personalInfoEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="Your phone number" data-id="element-5565" />
                      <PhoneIcon className="absolute left-3 top-2.5 h-5 w-5 text-green-500" data-id="element-5566" />
                    </div>
                    {phoneError ? <p className="text-red-500 text-xs mt-1" data-id="element-5567">{phoneError}</p> : <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5568">
                        Use international format, e.g., +44 20 7946 0018.
                      </p>}
                  </div>
                </div>
              </div>
              {/* Address Section */}
              <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2 relative flex flex-col`} data-id="element-5569">
                <div className="flex justify-between items-center mb-4" data-id="element-5570">
                  <h2 className="text-xl font-serif font-semibold" data-id="element-5571">
                    Address Information
                  </h2>
                  <div className="flex gap-2" data-id="element-5572">
                    {addressEditMode && <button onClick={handleSubmit} disabled={isSaving || !!zipCodeError} className="px-3 py-1 rounded-md bg-green-500 text-white hover:bg-green-600 text-sm flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5573">
                        {isSaving ? <>
                            <LoaderIcon className="h-3.5 w-3.5 mr-1 animate-spin" data-id="element-5574" />
                            Saving...
                          </> : <>
                            <SaveIcon className="h-3.5 w-3.5 mr-1" data-id="element-5575" />
                            Save Changes
                          </>}
                      </button>}
                    <button onClick={() => handleEditModeToggle('address')} className="px-3 py-1 rounded-md bg-transparent border border-green-500 text-green-500 hover:bg-green-500 hover:text-white text-sm flex items-center transition-colors" data-id="element-5576">
                      {addressEditMode ? <>
                          <XIcon className="h-3.5 w-3.5 mr-1" data-id="element-5577" />
                          Cancel
                        </> : <>
                          <EditIcon className="h-3.5 w-3.5 mr-1" data-id="element-5578" />
                          Edit
                        </>}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-grow" data-id="element-5579">
                  <div className="md:col-span-2" data-id="element-5580">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5581">
                      Street Address
                    </label>
                    <div className="relative" data-id="element-5582">
                      <input type="text" value={street} onChange={e => setStreet(e.target.value)} disabled={!addressEditMode} className={`w-full pl-10 pr-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!addressEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="Street address" data-id="element-5583" />
                      <MapPinIcon className="absolute left-3 top-2.5 h-5 w-5 text-red-500" data-id="element-5584" />
                    </div>
                  </div>
                  {/* Address Line 2 */}
                  <div className="md:col-span-2" data-id="element-5585">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5586">
                      Address Line 2 (Apartment, Unit, Suite, etc.)
                    </label>
                    <input type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} disabled={!addressEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!addressEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="Apartment, unit, suite, etc. (optional)" data-id="element-5587" />
                  </div>
                  <div data-id="element-5588">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5589">
                      City
                    </label>
                    <input type="text" value={city} onChange={e => setCity(e.target.value)} disabled={!addressEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!addressEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="City" data-id="element-5590" />
                  </div>
                  <div data-id="element-5591">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5592">
                      ZIP / Postal Code
                    </label>
                    <input type="text" value={zipCode} onChange={handleZipCodeChange} disabled={!addressEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${zipCodeError ? 'border-red-500' : ''} ${!addressEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="ZIP / Postal code" data-id="element-5593" />
                    {zipCodeError && <p className="text-red-500 text-xs mt-1" data-id="element-5594">
                        {zipCodeError}
                      </p>}
                  </div>
                  <div data-id="element-5595">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5596">
                      State / Province
                    </label>
                    <input type="text" value={state} onChange={e => setState(e.target.value)} disabled={!addressEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!addressEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} placeholder="State / Province" data-id="element-5597" />
                  </div>
                  <div data-id="element-5598">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5599">
                      Country
                    </label>
                    <select value={country} onChange={e => setCountry(e.target.value)} disabled={!addressEditMode} className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${!addressEditMode ? 'opacity-75 cursor-not-allowed' : ''}`} data-id="element-5600">
                      <option value="" data-id="element-5601">Select a country</option>
                      <option value="US" data-id="element-5602">United States</option>
                      <option value="CA" data-id="element-5603">Canada</option>
                      <option value="UK" data-id="element-5604">United Kingdom</option>
                      <option value="AU" data-id="element-5605">Australia</option>
                      <option value="FR" data-id="element-5606">France</option>
                      <option value="DE" data-id="element-5607">Germany</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ID Verification Section */}
              <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1 flex flex-col`} data-id="element-5608">
                <h2 className="text-xl font-serif font-semibold mb-4" data-id="element-5609">
                  ID Verification
                </h2>
                <div className={`flex-1 p-5 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} flex flex-col items-center justify-center text-center`} data-id="element-5610">
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'} mb-3`} data-id="element-5611">
                    Coming Soon
                  </div>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-1 font-medium`} data-id="element-5612">
                    Verify your identity
                  </p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5613">
                    Identity verification helps build trust in our community and
                    will be available in a future update.
                  </p>
                </div>
              </div>
            </div>
          </>}
      </div>

      {/* Delete Banner Confirmation Modal */}
      {showDeleteBannerConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-5614">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl shadow-xl max-w-md w-full p-6`} data-id="element-5615">
            <div className="flex items-start mb-4" data-id="element-5616">
              <div className="flex-shrink-0" data-id="element-5617">
                <AlertCircleIcon className="h-6 w-6 text-red-500" data-id="element-5618" />
              </div>
              <div className="ml-3 flex-1" data-id="element-5619">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-5620">
                  Delete Banner Image
                </h3>
                <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5621">
                  Are you sure you want to delete this banner image? This action
                  cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-5622">
              <button onClick={() => setShowDeleteBannerConfirm(false)} className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-5623">
                Cancel
              </button>
              <button onClick={() => {
            setShowDeleteBannerConfirm(false);
            handleDeleteBanner();
          }} className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors" data-id="element-5624">
                Delete
              </button>
            </div>
          </div>
        </div>}

      {/* Delete Profile Photo Confirmation Modal */}
      {showDeletePhotoConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-5625">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl shadow-xl max-w-md w-full p-6`} data-id="element-5626">
            <div className="flex items-start mb-4" data-id="element-5627">
              <div className="flex-shrink-0" data-id="element-5628">
                <AlertCircleIcon className="h-6 w-6 text-red-500" data-id="element-5629" />
              </div>
              <div className="ml-3 flex-1" data-id="element-5630">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-5631">
                  Delete Profile Photo
                </h3>
                <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5632">
                  Are you sure you want to delete this photo? This action cannot
                  be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-5633">
              <button onClick={() => setShowDeletePhotoConfirm(false)} className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-5634">
                Cancel
              </button>
              <button onClick={() => {
            setShowDeletePhotoConfirm(false);
            handleDeleteProfilePhoto();
          }} className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors" data-id="element-5635">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </main>;
};